import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import {
  /**
   * This `useEffect` usage has been reviewed and documented.
   */
  // eslint-disable-next-line no-restricted-imports
  useEffect,
  useId,
  useState,
} from 'react';

import { FormField, useDebounce } from 'common';

import { type SearchBarOption } from '../../../types.ts';

interface SearchBarProps {
  label: string;
  onSelect: (id: string) => void;
  onDebouncedInputEffect?: (input: string) => void;
  options: SearchBarOption[];
  placeholder?: string;
  disabled?: boolean;
}

export function NodeSearchBar(prop: SearchBarProps) {
  const { label, placeholder, options, disabled, onSelect, onDebouncedInputEffect } = prop;
  const [input, setInput] = useState<string>('');
  const debouncedInput = useDebounce(input);

  useEffect(() => {
    /**
     * This effect attempts to sync the debounced input value with an external state.
     * `useEffect` review
     * Guidelines: https://notable-health.atlassian.net/wiki/spaces/notableengineering/pages/2566389961/React+State+Management#Required-Review
     *
     * This has been reviewed by a UI tech lead. It is a bit questionable, but approved.
     * It runs the risk of destroying upstream state, and potentially causing infinite loops, but in practice it has not been observed to do so.
     */
    onDebouncedInputEffect?.(debouncedInput);
  }, [debouncedInput, onDebouncedInputEffect]);

  const fieldId = useId();

  return (
    <FormField id={fieldId} label={label}>
      <Autocomplete<SearchBarOption>
        id={fieldId}
        disabled={disabled}
        forcePopupIcon={false}
        sx={{ flex: 1, alignContent: 'center' }}
        options={options}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option) => option.name}
        onChange={(_, newValue) => {
          if (newValue) {
            onSelect(newValue.id);
            setInput('');
          }
        }}
        inputValue={input}
        onInputChange={(e, newInputValue, reason) => {
          if (reason === 'input') {
            setInput(newInputValue);
          }
        }}
        renderInput={(params) => (
          <TextField {...params} variant="outlined" placeholder={placeholder} />
        )}
      />
    </FormField>
  );
}
